import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    createVacation,
    updateVacation,
    deleteVacation,
    Vacation,
    VacationInput,
    getUserTrips,
    TripInfo
} from '../../services/api';
import { VacationDefaults } from './CalendarSection';
import './VacationModal.css';

interface VacationModalProps {
    vacation?: Vacation | null;
    defaultValues?: VacationDefaults | null;
    onClose: () => void;
    onSaved: () => void;
}

export default function VacationModal({ vacation, defaultValues, onClose, onSaved }: VacationModalProps) {
    const { t } = useTranslation();
    const isEditing = !!vacation;

    // Form state - use vacation data for editing, defaultValues for new, or empty
    const [title, setTitle] = useState(vacation?.title || defaultValues?.title || '');
    const [startDate, setStartDate] = useState(vacation?.startDate || defaultValues?.startDate || '');
    const [endDate, setEndDate] = useState(vacation?.endDate || defaultValues?.endDate || '');
    const [type, setType] = useState<Vacation['type']>(vacation?.type || 'vacation');
    const [status, setStatus] = useState<Vacation['status']>(vacation?.status || 'planned');
    const [itineraryId, setItineraryId] = useState<string | null>(vacation?.itineraryId || defaultValues?.itineraryId || null);

    // UI state
    const [trips, setTrips] = useState<TripInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Load user trips for itinerary selection
    useEffect(() => {
        getUserTrips()
            .then(data => setTrips(data.trips || []))
            .catch(() => setTrips([]));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        const input: VacationInput = {
            title: title.trim(),
            startDate,
            endDate,
            type,
            status,
            itineraryId: itineraryId || null,
        };

        try {
            if (isEditing && vacation) {
                await updateVacation(vacation.id, input);
            } else {
                await createVacation(input);
            }
            onSaved();
        } catch (err: any) {
            setError(err.error || t('calendar.error_invalid_date_range'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!vacation) return;
        setLoading(true);
        try {
            await deleteVacation(vacation.id);
            onSaved();
        } catch (err: any) {
            setError(err.error || 'Failed to delete');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="vacation-modal-overlay" onClick={onClose}>
            <div className="vacation-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEditing ? t('calendar.edit_vacation') : t('calendar.add_vacation')}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && <div className="error-banner">{error}</div>}

                    <div className="form-group">
                        <label>{t('calendar.field_title')}</label>
                        <input
                            type="text"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            maxLength={100}
                            required
                            placeholder="Summer in Paris..."
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('calendar.field_start_date')}</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('calendar.field_end_date')}</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('calendar.field_type')}</label>
                            <select value={type} onChange={e => setType(e.target.value as Vacation['type'])}>
                                <option value="vacation">{t('calendar.type_vacation')}</option>
                                <option value="city_break">{t('calendar.type_city_break')}</option>
                                <option value="road_trip">{t('calendar.type_road_trip')}</option>
                                <option value="workation">{t('calendar.type_workation')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t('calendar.field_status')}</label>
                            <select value={status} onChange={e => setStatus(e.target.value as Vacation['status'])}>
                                <option value="planned">{t('calendar.status_planned')}</option>
                                <option value="confirmed">{t('calendar.status_confirmed')}</option>
                                <option value="finished">{t('calendar.status_finished')}</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>{t('calendar.field_itinerary')}</label>
                        <select
                            value={itineraryId || ''}
                            onChange={e => setItineraryId(e.target.value || null)}
                        >
                            <option value="">— {t('calendar.unlink_itinerary')} —</option>
                            {trips.map(tripInfo => (
                                <option key={tripInfo.trip.id} value={tripInfo.trip.id}>
                                    {tripInfo.content?.title || tripInfo.trip.slug}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="modal-actions">
                        {isEditing && !showDeleteConfirm && (
                            <button
                                type="button"
                                className="delete-btn"
                                onClick={() => setShowDeleteConfirm(true)}
                            >
                                {t('calendar.delete_vacation')}
                            </button>
                        )}

                        {showDeleteConfirm && (
                            <div className="delete-confirm">
                                <span>{t('calendar.delete_confirm')}</span>
                                <button type="button" className="confirm-delete" onClick={handleDelete}>
                                    {t('calendar.delete_vacation')}
                                </button>
                                <button type="button" onClick={() => setShowDeleteConfirm(false)}>
                                    {t('common.cancel')}
                                </button>
                            </div>
                        )}

                        {!showDeleteConfirm && (
                            <div className="save-actions">
                                <button type="button" className="cancel-btn" onClick={onClose}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="save-btn" disabled={loading}>
                                    {loading ? t('common.processing') : t('common.save')}
                                </button>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
